import { type Show } from '../../api/shows';
import { formatDateIST, NotFoundImage } from '../../utils/function';
import Modal from '../model/Modal';

interface ShowDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  show: Show | null;
}

export default function ShowDetails({ isOpen, onClose, show, onEdit, onDelete }: ShowDetailsProps) {
  if (!show) return null;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
      onClose();
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Show Details"
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end space-x-3">
          {onDelete && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-red-400 hover:text-white hover:bg-red-500/10 rounded-md transition-colors flex items-center gap-2"
            >
              <span>Delete</span>
            </button>
          )}
          {onEdit && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-cyan-400 hover:text-white hover:bg-cyan-500/10 rounded-md transition-colors flex items-center gap-2"
            >
              <span>Edit</span>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-medium text-white">Show Information</h3>
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-4">
              <div>
                <p className="text-sm text-gray-400">Name</p>
                <p className="text-white">{show.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Host</p>
                <p className="text-white">{show.hostName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${show.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                  {show.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <h3 className="text-lg font-medium text-white">Thumbnail</h3>
            <div className="bg-gray-800/50 rounded-lg p-4 flex justify-center">
              <img
                src={show.thumbnail || NotFoundImage}
                alt={show.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = NotFoundImage;
                }}
              />
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <h3 className="text-lg font-medium text-white">Description</h3>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-gray-300 whitespace-pre-line">
              {show.description || 'No description available'}
            </p>
          </div>
        </div>

        {show.categories && show.categories.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-lg font-medium text-white">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {show.categories.map(category => (
                <span
                  key={category.id}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300"
                >
                  {category.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-400">Created At</h3>
            <p className="text-white">
              {show.createdAt ? formatDateIST(show.createdAt) : 'N/A'}
            </p>
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-gray-400">Last Updated</h3>
            <p className="text-white">
              {show.updatedAt ? formatDateIST(show.updatedAt) : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
